import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import Ipucu from './Ipucu';

/**
 * CARİ KARTINDAKİ KASA KUTUSU — carinin kasa hareketleri ve emanet bakiyesi.
 * Yalnız `cash.view` yetkisinde çizilir (çağıran denetler). Gayriresmî hesaptaki
 * hareketler, onları göremeyen role sunucudan hiç gelmez.
 */
type Satir = { id: number; tarih: string; yon: 'GIRIS' | 'CIKIS'; tutarKurus: number; para: string; hesapAd: string; kategoriAd: string; aciklama: string | null; emanet: boolean };
type Emanet = { para: string; netKurus: number; yon: 'BIZDE' | 'ALACAK' | 'KAPALI' };
type Veri = { emanet: Emanet[]; hareketler: Satir[]; toplam: number; girisKurus: number; cikisKurus: number; paralar: string[] };

const para = (kurus: number, birim: string) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: birim, minimumFractionDigits: kurus % 100 === 0 ? 0 : 2, maximumFractionDigits: kurus % 100 === 0 ? 0 : 2,
}).format(kurus / 100);
const gun = (g: string) => `${g.slice(8, 10)}.${g.slice(5, 7)}.${g.slice(0, 4)}`;

export default function CariKasa({ cariId }: { cariId: number }) {
  const [v, setV] = useState<Veri | null>(null);
  useEffect(() => { setV(null); api<Veri>(`/api/kasa/cari/${cariId}`).then(setV).catch(() => undefined); }, [cariId]);
  // Hiç kasa hareketi olmayan caride kutu yer kaplamaz.
  if (!v || v.toplam === 0) return null;
  const acik = v.emanet.filter((e) => e.yon !== 'KAPALI');
  const tekPara = v.paralar.length === 1 ? v.paralar[0] : null;
  return (
    <div className="ccd-card ccd-stack-sm">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="ccd-fieldset-title">Kasa hareketleri ({v.toplam})</div>
        <Link className="ccd-btn ccd-btn-ghost ccd-btn-sm" to="/kasa?sekme=hareketler">Kasa ve Banka →</Link>
      </div>
      <div className="ccd-kasa-cari-ozet">
        {acik.length === 0
          ? <span className="ccd-meta">Emanet bakiyesi yok <Ipucu id="emanet-bakiyesi" /></span>
          : acik.map((e) => (
            <span key={e.para} className={`ccd-kasa-yon ccd-kasa-yon-${e.yon === 'BIZDE' ? 'bizde' : 'alacak'}`} data-fihrist="emanet-bakiyesi">
              Emanet: <b>{para(Math.abs(e.netKurus), e.para)}</b> {e.yon === 'BIZDE' ? 'bizde duran' : 'bizim alacağımız'}
            </span>
          ))}
        {acik.length > 0 && <Ipucu id="emanet-bakiyesi" />}
        {tekPara && <span className="ccd-meta">Toplam giriş {para(v.girisKurus, tekPara)} · çıkış {para(v.cikisKurus, tekPara)}</span>}
      </div>
      <div className="ccd-table-wrap">
        <table className="ccd-table">
          <thead><tr>
            <th className="ccd-th">Tarih</th><th className="ccd-th">İşlem</th><th className="ccd-th">Hesap</th>
            <th className="ccd-th ccd-td-num">Giriş</th><th className="ccd-th ccd-td-num">Çıkış</th>
          </tr></thead>
          <tbody>
            {v.hareketler.map((h) => (
              <tr key={h.id} className="ccd-tr">
                <td className="ccd-td ccd-td-nowrap">{gun(h.tarih)}</td>
                <td className="ccd-td">{h.kategoriAd}{h.emanet && <span className="ccd-badge ccd-badge-accent" style={{ marginLeft: 6 }}>emanet</span>}{h.aciklama && <div className="ccd-meta">{h.aciklama}</div>}</td>
                <td className="ccd-td">{h.hesapAd}</td>
                <td className="ccd-td ccd-td-num ccd-kasa-giris">{h.yon === 'GIRIS' ? para(h.tutarKurus, h.para) : ''}</td>
                <td className="ccd-td ccd-td-num ccd-kasa-cikis">{h.yon === 'CIKIS' ? para(h.tutarKurus, h.para) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {v.toplam > v.hareketler.length && <div className="ccd-meta">Son {v.hareketler.length} hareket gösteriliyor; tamamı Kasa ve Banka › Hareketler'de.</div>}
    </div>
  );
}
